// Agent achievements — derived from the profile data the server
// already hydrates on /profile/:username. No table, no backfill,
// no cron: the "achievement" is just "did the agent meet the
// threshold right now."
//
// Deliberately separate from the human achievement system
// (SoulboundAchievements, etc.) so:
//   - Human taxonomy stays focused on human milestones
//     (beta_survivor, og_founder, spotlight streaks, KlyxValue tiers)
//   - Agent taxonomy is invocation- + capability-shaped
//   - Neither surface has to reason about the other's threshold rules
//
// v1 achievement set covers what's shipped:
//   Receipts   (Ring 3 Phase 1 count)
//   Chains     (declared array on app.agent)
//
// Scaffolded ahead of the backend (definitions live here today,
// input field defaults to 0 so every chip stays unearned until the
// backend hydrates a real count):
//   Verified endpoints (ADR-012 — verification bundle, Phase 1)
//
// Deferred until the relevant phase lands:
//   Verification tier badges (ADR-012 — org/DNS/email tier surface)
//   Top-percentile AgentValue (Ring 3 Phase 2 — reputation formula)
//   Dispute-clean streaks (ADR-014 — dispute model)
//
// When those ship, add new AchievementDefinition rows here + the
// component picks them up automatically.

import { labelForChain } from '../components/AgentChainChips/agentChainLabels';

export type AchievementTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface AchievementInput {
  receiptCount: number;
  chains: string[];
  /** Count of the agent's endpoints that currently hold a verified
   *  badge (ADR-012 — endpoint challenge probe). Optional so the
   *  profile page can omit it until the backend serializes the field;
   *  treated as 0 when absent, which keeps the verified-endpoint
   *  chips visible-but-unearned in the meantime. */
  verifiedEndpointCount?: number;
}

export interface Achievement {
  id: string;
  label: string;
  description: string;
  tier: AchievementTier;
  earned: boolean;
}

interface AchievementDefinition {
  id: string;
  label: string;
  /** Short description shown in the tooltip. Rendered on hover so
   *  it should read as a single line — no line breaks. */
  description: (input: AchievementInput) => string;
  tier: AchievementTier;
  earned: (input: AchievementInput) => boolean;
}

// Total chain count for the "Universal" achievement. Kept as a
// derived constant so adding a new chain to AGENT_CHAINS
// automatically raises the bar. Import target is the client-side
// list — mirrors the server (see PAYMENT_ASSET_ALLOWED_CHAINS
// for the parallel structure).
import { AGENT_CHAINS } from '../components/AgentChainChips/agentChainLabels';
const ALL_CHAINS_COUNT = AGENT_CHAINS.length;

const DEFINITIONS: AchievementDefinition[] = [
  // ── Receipt milestones ───────────────────────────────────────
  {
    id: 'receipts-1',
    label: 'First Call',
    description: () => 'Received your first invocation receipt.',
    tier: 'bronze',
    earned: ({ receiptCount }) => receiptCount >= 1,
  },
  {
    id: 'receipts-10',
    label: 'Warmed Up',
    description: () => '10 invocation receipts logged.',
    tier: 'silver',
    earned: ({ receiptCount }) => receiptCount >= 10,
  },
  {
    id: 'receipts-100',
    label: 'Well-Traveled',
    description: () => '100 invocation receipts logged.',
    tier: 'gold',
    earned: ({ receiptCount }) => receiptCount >= 100,
  },
  {
    id: 'receipts-1000',
    label: 'Prolific',
    description: () => '1,000 invocation receipts logged.',
    tier: 'platinum',
    earned: ({ receiptCount }) => receiptCount >= 1000,
  },

  // ── Verified endpoints (ADR-012) ─────────────────────────────
  // Definitions live here today; every agent scores 0 until the
  // backend hydrates verifiedEndpointCount on the profile response.
  // Tiers mirror the "collect them all" gradient used elsewhere:
  // one, three, and every-declared-endpoint verified.
  {
    id: 'verified-endpoints-1',
    label: 'First Verified',
    description: () => 'First endpoint passed the verification challenge.',
    tier: 'bronze',
    earned: ({ verifiedEndpointCount }) => (verifiedEndpointCount ?? 0) >= 1,
  },
  {
    id: 'verified-endpoints-3',
    label: 'Trusted Surface',
    description: () => 'Three endpoints verified.',
    tier: 'silver',
    earned: ({ verifiedEndpointCount }) => (verifiedEndpointCount ?? 0) >= 3,
  },
  {
    id: 'verified-endpoints-10',
    label: 'Verified Fleet',
    description: () => 'Ten endpoints verified.',
    tier: 'gold',
    earned: ({ verifiedEndpointCount }) => (verifiedEndpointCount ?? 0) >= 10,
  },

  // ── Chain diversity ─────────────────────────────────────────
  {
    id: 'chains-3',
    label: 'Multichain',
    description: ({ chains }) =>
      `Operates on ${chains.length} chains (${chains.map(labelForChain).join(', ')}).`,
    tier: 'silver',
    earned: ({ chains }) => chains.length >= 3,
  },
  {
    id: 'chains-all',
    label: 'Universal',
    description: () =>
      `Operates on every supported chain (${AGENT_CHAINS.map(labelForChain).join(', ')}).`,
    tier: 'gold',
    earned: ({ chains }) => chains.length >= ALL_CHAINS_COUNT,
  },
];

/**
 * Compute the current achievement state for an agent. Returns the
 * full definition set with `earned` filled in — callers can filter
 * to earned-only or render all-with-muted-unearned as they prefer.
 *
 * Order is definition order (receipts then chains), not earned-first
 * — a stable render order avoids surprise reordering when a threshold
 * flips.
 */
export function computeAgentAchievements(
  input: AchievementInput,
): Achievement[] {
  return DEFINITIONS.map((def) => ({
    id: def.id,
    label: def.label,
    description: def.description(input),
    tier: def.tier,
    earned: def.earned(input),
  }));
}
